import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "./ui/select";
import { toast } from "sonner";
import { INDUSTRIES } from "../data/site";
import { PRODUCTS } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "", company: "", country: "", email: "", phone: "",
  industry: "", product_interest: "", project_requirement: ""
};

// NOTE: Declared at module scope on purpose. If moved inside EnquiryForm,
// every re-render creates a new component type — inputs remount and lose
// focus on each keystroke (the bug we fixed).
const Field = ({ id, label, required, error, children }) => (
  <div>
    <Label htmlFor={id} className="font-mono-wm text-[10px] tracking-[0.25em] uppercase text-[#475569]">
      {label} {required && <span className="text-[#0EA5E9]">*</span>}
    </Label>
    <div className="mt-2">{children}</div>
    {error && (
      <p className="text-[11px] text-[#DC2626] mt-1 font-mono-wm uppercase tracking-wider">{error}</p>
    )}
  </div>
);

const baseInput = "h-11 bg-white border-[#E2E8F0] text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] rounded-sm";

export const EnquiryForm = ({ defaultProduct }) => {
  const [form, setForm] = useState({ ...initial, product_interest: defaultProduct || "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    ["name", "company", "country", "email", "phone", "industry", "product_interest"].forEach((k) => {
      if (!form[k] || String(form[k]).trim().length === 0) e[k] = "Required";
    });
    if (form.project_requirement.trim().length < 5) e.project_requirement = "Please add a brief description";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Invalid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSuccess(true);
      setForm({ ...initial });
      toast.success("Enquiry submitted. Our team will be in touch shortly.");
    } catch (err) {
      console.error(err);
      toast.error("Submission failed. Please try again or email sales@clangrp.com");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="enquiry-success" className="border border-[#E2E8F0] bg-white p-10 text-center shadow-[0_8px_32px_-16px_rgba(15,23,42,0.12)]">
        <div className="w-14 h-14 mx-auto bg-[#ECFEFF] border border-[#0EA5E9] flex items-center justify-center mb-6">
          <CheckCircle2 className="w-7 h-7 text-[#0EA5E9]" />
        </div>
        <h3 className="font-display uppercase text-[#0F172A] text-2xl md:text-3xl tracking-tight">Enquiry Received</h3>
        <p className="text-[#475569] mt-4 max-w-md mx-auto leading-relaxed">
          Our engineering &amp; sales team will respond within 24 business hours. For urgent
          matters, write to <a className="text-[#0EA5E9] hover:underline" href="mailto:sales@clangrp.com">sales@clangrp.com</a>.
        </p>
        <button
          onClick={() => setSuccess(false)}
          data-testid="enquiry-reset"
          className="mt-8 font-mono-wm text-xs tracking-[0.25em] uppercase text-[#64748B] hover:text-[#0F172A]"
        >
          Submit another enquiry →
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} data-testid="enquiry-form" className="border border-[#E2E8F0] bg-white p-6 md:p-8 space-y-5 shadow-[0_8px_32px_-16px_rgba(15,23,42,0.10)]">
      <div className="grid md:grid-cols-2 gap-5">
        <Field id="name" label="Full Name" required error={errors.name}>
          <Input id="name" data-testid="enquiry-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={baseInput} placeholder="John Doe" />
        </Field>
        <Field id="company" label="Company" required error={errors.company}>
          <Input id="company" data-testid="enquiry-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={baseInput} placeholder="Company name" />
        </Field>
        <Field id="email" label="Work Email" required error={errors.email}>
          <Input id="email" type="email" data-testid="enquiry-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={baseInput} placeholder="you@company.com" />
        </Field>
        <Field id="phone" label="Phone" required error={errors.phone}>
          <Input id="phone" data-testid="enquiry-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={baseInput} placeholder="+91 XXXXX XXXXX" />
        </Field>
        <Field id="country" label="Country" required error={errors.country}>
          <Input id="country" data-testid="enquiry-country" value={form.country} onChange={(e) => set("country", e.target.value)} className={baseInput} placeholder="India" />
        </Field>
        <Field id="industry" label="Industry" required error={errors.industry}>
          <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
            <SelectTrigger data-testid="enquiry-industry" className={`${baseInput} h-11`}>
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent className="bg-white border-[#E2E8F0] text-[#0F172A]">
              {INDUSTRIES.map((i) => (
                <SelectItem key={i.id} value={i.name} className="focus:bg-[#ECFEFF] focus:text-[#0F172A]">{i.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field id="product_interest" label="Product Interest" required error={errors.product_interest}>
        <Select value={form.product_interest} onValueChange={(v) => set("product_interest", v)}>
          <SelectTrigger data-testid="enquiry-product" className={`${baseInput} h-11`}>
            <SelectValue placeholder="Which system are you interested in?" />
          </SelectTrigger>
          <SelectContent className="bg-white border-[#E2E8F0] text-[#0F172A]">
            {PRODUCTS.map((p) => (
              <SelectItem key={p.slug} value={p.name} className="focus:bg-[#ECFEFF] focus:text-[#0F172A]">{p.name}</SelectItem>
            ))}
            <SelectItem value="Custom Automation" className="focus:bg-[#ECFEFF] focus:text-[#0F172A]">Custom Automation Solution</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field id="project_requirement" label="Project Requirement" required error={errors.project_requirement}>
        <Textarea
          id="project_requirement"
          data-testid="enquiry-requirement"
          value={form.project_requirement}
          onChange={(e) => set("project_requirement", e.target.value)}
          className="min-h-[120px] bg-white border-[#E2E8F0] text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] rounded-sm"
          placeholder="Describe your application, pipe sizes, materials, tonnage, timeline, etc."
        />
      </Field>

      <div className="flex flex-col md:flex-row md:items-center gap-4 pt-1">
        <Button
          type="submit"
          disabled={loading}
          data-testid="enquiry-submit"
          className="group h-12 px-7 bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-sm uppercase tracking-[0.2em] rounded-sm flex items-center gap-3 disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
          {loading ? "Submitting…" : "Request Technical Consultation"}
        </Button>
        <p className="text-xs text-[#64748B] font-mono-wm tracking-wider">
          We respond within 24 business hours.
        </p>
      </div>
    </form>
  );
};

export default EnquiryForm;
